import React, { useState, useEffect } from 'react';
import { carouselItems } from '../data/mediaData';

export const MediaCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showText, setShowText] = useState(false);

  // Handle slide change and text animation
  useEffect(() => {
    // Immediately hide text (no fade out)
    setShowText(false);

    // Wait for image to be visible, then show text with slower animation
    const textTimer = setTimeout(() => {
      setShowText(true);
    }, 500);

    return () => {
      clearTimeout(textTimer);
    };
  }, [currentIndex]);

  useEffect(() => {
    // Auto-advance carousel every 5 seconds
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % carouselItems.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % carouselItems.length);
  };

  const currentItem = carouselItems[currentIndex];

  return (
    <div className="relative w-full max-w-5xl mx-auto my-6">
      <div className="relative overflow-hidden rounded-xl shadow-2xl bg-gray-100">
        {/* Main Carousel Content */}
        <div className="relative h-[350px] md:h-[450px]">
          {/* Image/Video */}
          <div className="absolute inset-0">
            {currentItem.type === 'image' ? (
              <img
                src={currentItem.src}
                alt={currentItem.alt}
                className="w-full h-full object-cover"
              />
            ) : (
              <video
                src={currentItem.src}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
              />
            )}
          </div>

          {/* Overlay Gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/20" />

          {/* Animated Text - From Bottom */}
          <div className="absolute inset-0 flex items-end justify-center pb-16 md:pb-20 px-8 md:px-12">
            {showText && (
              <div
                className="animate-slide-up"
                style={{ 
                  animation: 'slideUpFade 1.5s cubic-bezier(0.4, 0, 0.2, 1) forwards'
                }}
              >
                <p className="text-white text-2xl md:text-4xl font-bold text-center leading-relaxed drop-shadow-2xl">
                  {currentItem.text}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-200 hover:scale-110"
          aria-label="Previous slide"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-200 hover:scale-110"
          aria-label="Next slide"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {carouselItems.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MediaCarousel;
